import React, {useState} from 'react';
import {Pressable, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {Link} from '@react-navigation/native';
import {Formik, FormikHelpers} from 'formik';
import {observer} from 'mobx-react-lite';
import * as Yup from 'yup';

import BackButton from '../../components/rewards/BackButton';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import Otp from '../../components/ui-controls/form/Otp';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';

import {styles} from './styles';
import {VerificationSchema} from './validationSchema';

/**
 * Timeout for sending the code
 */
const TIMEOUT = 30000;

/**
 * Disable the resend link until this much time
 */
const RESEND_TIMEOUT = 10000;

/**
 * Wait for [delay] ms if [promise] returns first.
 * @param promise
 * @param delay
 * @returns
 */
async function slowDown(promise: Promise<any>, delay: number = RESEND_TIMEOUT) {
  const [value] = await Promise.all([
    promise,
    new Promise(resolve => {
      setTimeout(resolve, delay);
    }),
  ]);

  return value;
}

const RewardsVerificationScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_VERIFICATION>) => {
    const {rewardsStore} = useStores();

    const [resending, setResending] = useState(false);
    const [codeResent, setCodeResent] = useState(false);

    const phoneNumber = rewardsStore.phoneNumber || '';

    const onSubmit = async (
      values: VerificationValues,
      {setFieldError, setSubmitting}: FormikHelpers<VerificationValues>,
    ) => {
      setSubmitting(true);

      if (await rewardsStore.signUp(values.verification)) {
        navigation.navigate(AppRoute.REWARDS_SUCCESS);
      } else {
        setFieldError('verification', 'Wrong code entered. Please try again.');
      }

      setSubmitting(false);
    };

    const resendCode = (callback?: () => void) => async () => {
      callback?.();
      setResending(true);

      // apiService can stall so set timeout here
      const resendTimeout = setTimeout(() => {
        setResending(false);
        setCodeResent(false);
      }, TIMEOUT);

      try {
        // resend code can use rewardsVerifyExistingUserPhoneNumber
        // for existing and new user. there are no side effects
        const result = await slowDown(
          apiService.rewardsVerifyExistingUserPhoneNumber({
            phoneNumber: rewardsStore.phoneNumber,
          }),
        );

        const success = result?.status === 200;
        setCodeResent(success);
      } catch (e) {
        setCodeResent(false);
      }

      clearTimeout(resendTimeout);
      setResending(false);
    };

    const validationSchema = VerificationSchema;

    type VerificationValues = Yup.InferType<typeof validationSchema>;

    const defaultValues: VerificationValues = {
      verification: '',
    };

    return (
      <KeyboardAwareScrollView
        style={{backgroundColor: 'white', flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <Formik
          initialValues={defaultValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}>
          {({
            errors,
            handleChange,
            handleSubmit,
            handleBlur,
            touched,
            isSubmitting,
            resetForm,
            values,
          }) => {
            const handleSubmitPress = () => {
              handleSubmit();
            };
            const renderErrorMessageWhenTouchedOrPlaceholder = (
              name: keyof VerificationValues,
            ) => (
              <Text style={styles.errorMessage}>
                {touched[name] && errors[name] ? (errors[name] as string) : ' '}
              </Text>
            );

            return (
              <ContentView
                parentStyle={{flex: 1, paddingBottom: 16}}
                style={{flex: 1}}>
                <BackButton
                  onPress={() => {
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                    }
                  }}
                />
                <RewardsProgramImage variant="password" />
                <Text variant="heading1" style={{marginTop: 20}}>
                  {codeResent ? 'CODE RE-SENT' : 'WE’VE SENT THE CODE'}
                </Text>

                {codeResent ? (
                  <Text style={{marginBottom: 20}}>
                    We have re-sent your code to your mobile.
                  </Text>
                ) : null}

                <Text>
                  Please verify your number by entering the code sent to{' '}
                  {phoneNumber}.{' '}
                  <Link
                    style={{color: '#0054A6'}}
                    to={{screen: AppRoute.REWARDS_SIGN_IN}}>
                    Change number
                  </Link>
                </Text>

                <Text
                  variant="body2"
                  style={[
                    {marginTop: 20, marginBottom: 8},
                    !!errors.verification &&
                      touched.verification && {color: '#C81E1E'},
                  ]}>
                  Enter code:
                </Text>
                <Otp
                  value={values.verification}
                  numInputs={6}
                  onChangeText={handleChange('verification')}
                  onEnter={handleSubmitPress}
                  onBlur={() => {
                    handleBlur('verification');
                  }}
                  error={!!errors.verification && touched.verification}
                />
                {renderErrorMessageWhenTouchedOrPlaceholder('verification')}
                <View
                  style={{
                    backgroundColor: '#F9FAFB',
                    borderRadius: 8,
                    height: 52,
                    justifyContent: 'center',
                    paddingHorizontal: 12,
                  }}>
                  <Text variant="body2" style={{color: '#6B7280'}}>
                    Didn’t get a code?{' '}
                    {resending ? (
                      <Text variant="body2">Click to resend</Text>
                    ) : (
                      <Text
                        onPress={resendCode(() => resetForm())}
                        variant="body2"
                        style={{color: '#1A56DB'}}>
                        Click to resend
                      </Text>
                    )}
                  </Text>
                </View>

                <View style={{marginTop: 32}}>
                  <Button
                    title={'VERIFY'}
                    onPress={handleSubmitPress}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  />
                </View>
              </ContentView>
            );
          }}
        </Formik>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsVerificationScreen;
